import { useNavigate } from "react-router-dom"
import { CheckCircle2, ArrowRight, User, Mail, Phone } from "lucide-react"
import { Button } from "../../../components/ui/button"

const informatiqueContext = require.context("../../../assets/informatique", false, /\.(jpg|jpeg|png|webp)$/i)
const informatiqueKeys = informatiqueContext.keys()
const getFstImgSrc = (s) => (typeof s === "string" ? s : s?.default ?? s)
const FST_IMAGES = informatiqueKeys.slice(0, 22).map((key) => informatiqueContext(key))
const FST_HERO_SRC = FST_IMAGES.length > 0 ? getFstImgSrc(FST_IMAGES[2]) : null
const doyenPhotoKey = informatiqueKeys.find((k) => /doyen|Doyen/i.test(k))
const DOYEN_PHOTO = doyenPhotoKey ? informatiqueContext(doyenPhotoKey) : null

export default function SciencesTech() {
    const navigate = useNavigate()
    return (
        <div className="min-h-screen bg-background font-sans text-foreground">
            {/* Hero Section with background from assets/informatique */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {FST_HERO_SRC && (
                        <img
                            src={FST_HERO_SRC}
                            alt="Faculté des Sciences et Technologies"
                            className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-105"
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Sciences & Technologies</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Vol. 2024</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Façonnez le</span>
                            <span className="block text-white mt-2">Futur Tech</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Rejoignez une élite d'ingénieurs et maîtrisez le code et les sciences appliquées.
                                <span className="italic block mt-1 tracking-tight">Innover pour transformer.</span>
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button onClick={() => navigate("/admission")} className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Candidater Maintenant
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button onClick={() => navigate("/campus")} variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm">
                                Découvrir le Campus
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats - Newspaper Style Dateline */}
            {/* <div className="border-y border-border bg-secondary/10">
                <div className="container py-4 flex flex-wrap items-center gap-8">
                    {[
                        { val: "5", label: "Laboratoires" },
                        { val: "300+", label: "Projets" },
                        { val: "95%", label: "Insertion" },
                        { val: "1er", label: "Innovation" }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xl font-black text-primary">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                            {i < 3 && <span className="hidden lg:block text-border ml-4 font-light">/</span>}
                        </div>
                    ))}
                </div>
            </div> */}

            {/* Main Article - Magazine Layout */}
            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    {/* Sidebar - Meta Info */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">
                                Sommaire
                            </p>
                            {["Vision", "Doyen", "Organisation", "Objectifs", "Approche", "Évaluation", "Filières", "Contact"].map((item, i) => (
                                <a key={i} href={`#${item.toLowerCase()}`} className="group flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground hover:text-primary transition-smooth">
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item}
                                </a>
                            ))}
                        </nav>

                        <div className="mt-12 pt-8 border-t border-border">
                            <div className="bg-primary/5 p-6 border-l-4 border-primary">
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-4 text-primary">En Bref</h4>
                                <dl className="space-y-4 text-[11px]">
                                    <div className="flex justify-between items-baseline border-b border-border/40 pb-2">
                                        <dt className="font-bold">Durée</dt>
                                        <dd className="text-muted-foreground">3 ans (B1–B3)</dd>
                                    </div>
                                    <div className="flex justify-between items-baseline border-b border-border/40 pb-2">
                                        <dt className="font-bold">Crédits</dt>
                                        <dd className="text-muted-foreground">30 / semestre</dd>
                                    </div>
                                    <div className="flex justify-between items-baseline">
                                        <dt className="font-bold">Filières</dt>
                                        <dd className="text-muted-foreground">3</dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </aside>

                    {/* Main Body */}
                    <main className="space-y-16">
                        {/* Section 01 - Vision */}
                        <section id="vision">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Ingénierie du Futur
                            </h2>

                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    La Faculté des Sciences et Technologies de l&apos;Université de Ngozi assure une formation de haut niveau en sciences informatiques qui prépare au cycle de Master. L&apos;objectif est d&apos;assurer une maîtrise de la technologie et de former des acteurs capables de s&apos;adapter aux changements et d&apos;en être les initiateurs.
                                </p>
                            </div>

                            {/* Bento grid — Vision */}
                            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 auto-rows-[140px] md:auto-rows-[160px] gap-3">
                                {FST_IMAGES[1] && (
                                    <div className="col-span-2 row-span-2 rounded-xl overflow-hidden border border-border shadow-md bg-muted">
                                        <img src={getFstImgSrc(FST_IMAGES[1])} alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                {FST_IMAGES[9] && (
                                    <div className="col-span-2 rounded-xl overflow-hidden border border-border shadow-md bg-muted">
                                        <img src={getFstImgSrc(FST_IMAGES[9])} alt="Laboratoires et formation" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <div className="col-span-2 rounded-xl p-4 bg-secondary/5 border border-border/50 flex flex-col justify-center gap-2">
                                    {["Développement Logiciel", "Modélisation Mathématique", "Énergies Renouvelables"].map((skill, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                                            <span className="text-[10px] font-bold uppercase tracking-tight">{skill}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* Doyen a.i. — Mfura Armand */}
                        <section id="doyen">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Direction
                            </h2>
                            <div className="bg-gradient-to-br from-primary/5 to-accent/5 p-6 md:p-8 rounded-xl border border-border">
                                <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center md:items-start">
                                    {DOYEN_PHOTO && (
                                        <div className="flex-shrink-0 w-40 h-40 md:w-48 md:h-48 rounded-xl overflow-hidden border-2 border-border shadow-md bg-muted">
                                            <img src={getFstImgSrc(DOYEN_PHOTO)} alt="Mfura Armand" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                    <div className="flex-1 text-center md:text-left">
                                        <h3 className="text-xl font-bold text-foreground mb-1">Mfura Armand</h3>
                                        <p className="text-primary font-semibold mb-2">Professeur et Doyen a.i.</p>
                                        <p className="text-sm text-muted-foreground font-medium">Faculté des Sciences et Technologies</p>
                                        <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                                            La faculté est placée sous la direction du Professeur Mfura Armand, Doyen par intérim, qui pilote les formations en sciences et technologies au service de l&apos;innovation et du développement.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Organisation de la formation */}
                        <section id="organisation">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Organisation de la formation
                            </h2>
                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    La formation est étalée sur <strong>3 années de baccalauréat (B1, B2, B3)</strong> soit <strong>6 semestres</strong> de 30 crédits chacun (B1S1, B1S2, B2S1, B2S2, B3S1, B3S2). Un crédit vaut 25 heures de travail dont 15 heures de travail présentiel (cours magistral, travaux dirigés, travaux pratiques, stage accompagné) et 10 heures de travail personnel de l&apos;apprenant.
                                </p>
                                <p>La formation comporte :</p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>Des enseignements propres à la spécialisation et des cours d&apos;appui</li>
                                    <li>Des stages</li>
                                </ul>
                            </div>
                            {/* Bento — Organisation: 2 images side by side */}
                            {(FST_IMAGES[10] || FST_IMAGES[11]) && (
                                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {FST_IMAGES[10] && (
                                        <div className="md:col-span-2 rounded-xl overflow-hidden border border-border shadow-md aspect-[2/1] md:aspect-auto md:min-h-[200px]">
                                            <img src={getFstImgSrc(FST_IMAGES[10])} alt="Organisation de la formation" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                    {FST_IMAGES[11] && (
                                        <div className="rounded-xl overflow-hidden border border-border shadow-md aspect-[2/1] md:aspect-auto md:min-h-[200px]">
                                            <img src={getFstImgSrc(FST_IMAGES[11])} alt="Formation et stages" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>

                        {/* Objectif global et spécifiques */}
                        <section id="objectifs">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Objectifs de la formation
                            </h2>
                            <div className="space-y-6">
                                <div className="prose-sm text-muted-foreground font-medium leading-relaxed">
                                    <p className="font-semibold text-foreground mb-2">Objectif global</p>
                                    <p>
                                        Le bachelier en Sciences informatique doit être capable d&apos;utiliser les principes informatiques, les concepts et techniques pour concevoir, mettre en œuvre, gérer et maintenir les systèmes informatiques qui fournissent des solutions aux diverses activités pour le bénéfice de l&apos;humanité.
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-foreground mb-3">Objectifs académiques (Baccalauréat en Génie Logiciel)</p>
                                    <p className="text-sm text-muted-foreground mb-3">Le bachelier en Génie Logiciel doit être capable de :</p>
                                    <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
                                        <li>S&apos;approprier les concepts, paradigmes, langages, modèles de production, développement, maintenance, suivi, gestion, sécurité et assurance qualité des applications informatiques.</li>
                                        <li>S&apos;approprier les techniques d&apos;analyse, conception, développement, gestion des processus et maintenance des systèmes informatiques.</li>
                                        <li>Démontrer les connaissances et habiletés pratiques acquises en milieu professionnel.</li>
                                        <li>Répondre aux exigences « Qualité », « Temps » et « Coût » dans la mise en œuvre des systèmes d&apos;information.</li>
                                    </ul>
                                </div>
                            </div>
                            {FST_IMAGES[14] && (
                                <div className="mt-8 rounded-xl overflow-hidden border border-border shadow-md">
                                    <img src={getFstImgSrc(FST_IMAGES[14])} alt="Objectifs de la formation" className="w-full aspect-[21/9] object-cover" />
                                </div>
                            )}
                        </section>

                        {/* Approche pédagogique */}
                        <section id="approche">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Approche pédagogique
                            </h2>
                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    L&apos;Université de Ngozi a pour mission d&apos;assurer à ses étudiants une formation de haut niveau dans les sciences informatiques qui les prépare au cycle de Master. L&apos;inscription est assujettie à des conditions de niveau scientifique et de régularité en classe.
                                </p>
                                <p>
                                    Les enseignements sont dispensés sous forme de cours intégrés en classes, travaux dirigés et travaux pratiques en petits groupes, pour un encadrement et un suivi de qualité et une consolidation des aptitudes à la résolution de problèmes concrets.
                                </p>
                                <p>
                                    L&apos;Université de Ngozi offre des plates-formes (Cisco, Microsoft, SQL, Open source…) permettant aux étudiants d&apos;être préparés à des certifications internationalement reconnues dans les TIC.
                                </p>
                                <p>
                                    Parallèlement à leur formation académique, les étudiants peuvent s&apos;adonner à des activités socioculturelles et sportives. Forums, conférences, séminaires et voyages d&apos;études constituent autant de manifestations dont les étudiants peuvent prendre en charge l&apos;organisation.
                                </p>
                            </div>
                            {/* Bento — Approche: large + small */}
                            {(FST_IMAGES[12] || FST_IMAGES[13]) && (
                                <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-3">
                                    {FST_IMAGES[12] && (
                                        <div className="md:col-span-3 rounded-xl overflow-hidden border border-border shadow-md aspect-[2/1] md:aspect-auto md:min-h-[220px]">
                                            <img src={getFstImgSrc(FST_IMAGES[12])} alt="Approche pédagogique et plateformes" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                    {FST_IMAGES[13] && (
                                        <div className="rounded-xl overflow-hidden border border-border shadow-md aspect-[1/1] md:aspect-auto md:min-h-[220px]">
                                            <img src={getFstImgSrc(FST_IMAGES[13])} alt="Formation et certifications" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>

                        {/* Évaluation et validation */}
                        <section id="évaluation">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Évaluation et validation du diplôme
                            </h2>
                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>La délivrance du diplôme est subordonnée à :</p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>Une présence et une participation active des étudiants au programme</li>
                                    <li>L&apos;obtention des crédits nécessaires pour l&apos;ensemble des unités d&apos;enseignement obligatoires, transversaux et optionnel, y compris le rapport de stage</li>
                                    <li>Une évaluation sous deux formes : deux sessions normales semestrielles et une session de rattrapage annuelle</li>
                                    <li>Des épreuves dont la nature varie selon les matières : examen pratique, écrit et/ou oral, travail individuel ou de groupe, contrôle continu et examen terminal. Les coefficients correspondent aux crédits.</li>
                                </ul>
                            </div>
                        </section>

                        {/* Filières */}
                        <section id="filieres">
                            <h2 className="heading-4 text-foreground mb-8 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Filières disponibles
                            </h2>
                            <div className="grid md:grid-cols-3 gap-6">
                                {[
                                    { title: "Génie logiciel", en: "Software engineering", tags: ["Développement", "Maintenance", "Qualité", "Sécurité"] },
                                    { title: "Ingénierie des réseaux et systèmes", en: "Network and systems engineering", tags: ["Réseaux", "Systèmes", "Infrastructures"] },
                                    { title: "Informatique de gestion", en: "Management IT", tags: ["Gestion", "Systèmes d'information", "Business"] }
                                ].map((prog, i) => (
                                    <div key={i} className="interactive-card p-6 border-l-4 border-primary transition-smooth">
                                        <h3 className="heading-4 mb-2">{prog.title}</h3>
                                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-4">{prog.en}</p>
                                        <div className="flex flex-wrap gap-2">
                                            {prog.tags.map((tag, j) => (
                                                <span key={j} className="px-2 py-1 bg-secondary/50 text-[10px] font-bold uppercase tracking-wider rounded-sm text-primary">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Contact */}
                        <section id="contact">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Contact utile
                            </h2>
                            <div className="bg-gradient-to-br from-primary/5 to-accent/5 p-6 rounded-xl border border-border">
                                <p className="font-bold text-foreground mb-2">Eng. Armand MFURA</p>
                                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                    <a href="mailto:armand.mfura@univ-ngozi.bi" className="flex items-center gap-2 hover:text-primary transition-colors">
                                        <Mail className="w-4 h-4" />
                                        armand.mfura@univ-ngozi.bi
                                    </a>
                                    <a href="tel:+25779267219" className="flex items-center gap-2 hover:text-primary transition-colors">
                                        <Phone className="w-4 h-4" />
                                        +257 79 267 219
                                    </a>
                                </div>
                            </div>
                        </section>

                        {/* Bento gallery */}
                        {FST_IMAGES.length >= 6 && (
                            <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[100px] md:auto-rows-[120px] gap-2">
                                {FST_IMAGES[2] && (
                                    <div className="col-span-2 row-span-2 rounded-xl overflow-hidden border border-border shadow-md">
                                        <img src={getFstImgSrc(FST_IMAGES[2])} alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                {FST_IMAGES[3] && (
                                    <div className="rounded-xl overflow-hidden border border-border">
                                        <img src={getFstImgSrc(FST_IMAGES[3])} alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                {FST_IMAGES[4] && (
                                    <div className="rounded-xl overflow-hidden border border-border">
                                        <img src={getFstImgSrc(FST_IMAGES[4])} alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                {FST_IMAGES[10] && (
                                    <div className="col-span-2 rounded-xl overflow-hidden border border-border">
                                        <img src={getFstImgSrc(FST_IMAGES[10])} alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                {FST_IMAGES[18] && (
                                    <div className="rounded-xl overflow-hidden border border-border">
                                        <img src={getFstImgSrc(FST_IMAGES[18])} alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                {FST_IMAGES[19] && (
                                    <div className="rounded-xl overflow-hidden border border-border">
                                        <img src={getFstImgSrc(FST_IMAGES[19])} alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                            </div>
                            
                        )}

                    </main>
                </div>
            </article>

            {/* CTA Action - commented out */}
            <footer className="bg-primary text-white border-t-8 border-accent">
                <div className="container py-16 text-center max-w-2xl">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black mb-4 block">Prochaine Étape</span>
                    <h2 className="heading-2 mb-8 leading-tight">
                        Codez votre futur.<br />Innovez aujourd'hui.
                    </h2>
                    {/* <Button className="bg-accent text-accent-foreground px-10 py-4 text-base font-black hover:bg-accent/90 transition-smooth group rounded-md shadow-2xl border-b-4 border-black/10">
                        Postuler Maintenant
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button> */}
                </div>
            </footer>
            
        </div>
    )
}
